import React, {
  ReactNode,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';

import { Layout as CmsLayout, HeroSearch } from 'api/cms';
import { PublicPage } from 'api/website';
import { ArticleSearchHero } from 'components/ArticleSearchHero';
import { BannerAds } from 'components/BannerAds';
import { Box } from 'components/Box';
import { Button } from 'components/Button';
import Icon from 'components/Icon/Icon';
import { PageFooter } from 'components/Page/PageFooter';
import PageHeader from 'components/Page/PageHeader';
import PrintHidden from 'components/PrintHidden';
import { SearchHero } from 'components/Search/SearchHero';
import { H4 } from 'components/Text';
import HideContainer from 'containers/HideContainer';
import ScrollWatcherContainer from 'containers/ScrollWatcherContainer';
import BaseLayout from 'layouts/BaseLayout';
import { State } from 'store/main';
import { setWithSearchHero } from 'store/main/ducks/layout';
import { colors, gutterWidth, transition } from 'theme';
import AmpContext from 'utils/AmpContext';
import { trackEvent } from 'utils/analytics';
import { isBrowser } from 'utils/browser';
import { deleteCookie, getListCookie } from 'utils/cookies';
import { isDefined } from 'utils/functional';

import { LayoutContentToolbar } from './LayoutContentToolbar';
import { LayoutSearchMenu } from './LayoutSearchMenu';

const PageHeaderContainer = styled.div`
  width: 100%;
  left: 0;
  top: 0;
  position: relative;
  z-index: 102;
  box-shadow: none;
  pointer-events: none;
`;

type Props = {
  noAds?: boolean;
  buttonBar?: ReactNode;
  onHomePage?: boolean;
  onCms?: boolean;
  onStaffDashboard?: boolean;
  localeUrl?: Record<string, string>;
  navSection?: 'MAIN' | 'STAFF';
  defaultWithSearchHero?: boolean;
  layout?: CmsLayout;
  children?: ReactNode;
  searchMode?: boolean;
  minimalNav?: boolean;
  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  setUserInitiatedSearch?: (...args: Array<any>) => any;
  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onSearch?: (...args: Array<any>) => any;
  postSearchToolbar?: ReactNode;
  darkMode?: boolean;
  showListingCounts?: boolean;
  hideFooterDivider?: boolean;
  toolbar?: ReactNode;
  hero?: HeroSearch;
  content?: PublicPage;
  defaultSearchType?: DefaultSearchType;
};

export function Layout({
  noAds,
  buttonBar,
  content,
  localeUrl,
  navSection = 'MAIN',
  defaultWithSearchHero,
  layout,
  children,
  setUserInitiatedSearch,
  onSearch,
  minimalNav,
  postSearchToolbar,
  darkMode,
  showListingCounts,
  hideFooterDivider,
  toolbar,
  searchMode,
  hero,
  defaultSearchType,
  ...props
}: Props) {
  const { withSearchHero } = useSelector((state: State) => ({
    withSearchHero: state.layout.withSearchHero,
  }));

  const dispatch = useDispatch();

  const [searchOpen, setSearchOpen] = useState(false);
  const [pageUrlIsSet, setPageUrlIsSet] = useState(false);
  const [pageUrl, setPageUrl] = useState({
    en: '',
    es: '',
    pt: '',
  });

  const heroRef = useRef(null);
  const requestIsAmp = useContext(AmpContext);
  const [trackedScrollBottom, setTrackedScrollBottom] = useState(false);
  const queuedTrackers = getListCookie('trackEvents');

  useEffect(() => {
    queuedTrackers.forEach((e) => trackEvent(e.type));
    deleteCookie('trackEvents');
  }, [queuedTrackers]);

  useEffect(() => {
    if (
      isDefined(defaultWithSearchHero) &&
      defaultWithSearchHero !== withSearchHero
    ) {
      dispatch(setWithSearchHero(defaultWithSearchHero));
    }
  }, [defaultWithSearchHero, withSearchHero, dispatch]);

  useEffect(() => {
    if (isBrowser() && !pageUrlIsSet) {
      const urlToSet = localeUrl || {
        en: window.location.pathname,
        es: window.location.pathname,
        pt: window.location.pathname,
      };
      // @ts-expect-error ts-migrate(2345) FIXME: Argument of type 'Record<string, any>' is not assi... Remove this comment to see the full error message
      setPageUrl(urlToSet);
      setPageUrlIsSet(true);
    }
  }, [pageUrl, pageUrlIsSet, setPageUrl, setPageUrlIsSet, localeUrl]);

  const toggleSearch = (open = !searchOpen) => {
    setSearchOpen(open);
  };

  const renderWithSearchHero = isDefined(defaultWithSearchHero)
    ? defaultWithSearchHero
    : withSearchHero;
  const hideHeroSearch = isDefined(searchMode) && searchMode && !requestIsAmp;

  return (
    <BaseLayout
      buttonBar={buttonBar}
      stickyHeaderHeight={
        isDefined(postSearchToolbar)
          ? [135, 135, 135, 75]
          : [60, 60, 60, searchMode || !renderWithSearchHero ? 83 : null]
      }
      header={
        <>
          {!noAds ? <BannerAds /> : null}

          <PageHeaderContainer>
            <Box>
              <PrintHidden>
                <PageHeader
                  minimalNav={minimalNav}
                  url={pageUrl}
                  navSection={navSection}
                  requestIsAmp={requestIsAmp}
                  withSearchHero={renderWithSearchHero}
                  toggleSearch={toggleSearch}
                />
              </PrintHidden>
            </Box>
          </PageHeaderContainer>

          <LayoutContentToolbar
            content={content}
            hero={hero}
            searchMode={searchMode}
            renderWithSearchHero={renderWithSearchHero}
            postSearchToolbar={postSearchToolbar}
            onSearch={onSearch}
            navSection={navSection}
            defaultSearchType={defaultSearchType}
          />
        </>
      }
    >
      {toolbar}

      {hero && (
        <Box
          position="relative"
          zIndex={2}
          height={hideHeroSearch ? 0 : 'auto'}
          overflow={hideHeroSearch && 'hidden'}
          bg={darkMode && colors.fog}
        >
          <div ref={heroRef}>
            {defaultSearchType === 'CMS_PAGE' ? (
              <ArticleSearchHero
                title={hero.text}
                image={hero.image}
                imageMobile={hero.imageMobile}
                content={content}
              />
            ) : (
              <SearchHero
                title={hero.text}
                image={hero.image}
                imageMobile={hero.imageMobile}
                searchMode={hideHeroSearch}
                navSection={navSection}
                onSearch={onSearch}
                defaultSearchType={defaultSearchType}
                showListingCounts={showListingCounts}
              />
            )}
          </div>
        </Box>
      )}

      <PrintHidden>
        <HideContainer lg xl>
          <Box
            position="fixed"
            top={0}
            left={0}
            zIndex={102}
            transform={
              searchOpen
                ? 'translateY(0)'
                : 'translateY(-100%) translateY(-6px)'
            }
            width="100%"
            height="100%"
            transition={`all ${transition}`}
          >
            <Box
              classes={['flex', 'align-center', 'justify-between']}
              position="absolute"
              left={0}
              top={0}
              zIndex={1}
              px={gutterWidth / 2}
              height={60}
              width="100%"
              borderBottom={`1px solid ${colors.aluminum}`}
            >
              <H4 mb={0} color={colors.waterBlue}>
                {getText('Search')}
              </H4>

              <Button
                onClick={() => setSearchOpen(false)}
                aria-label={getText('Close search')}
                ml="auto"
              >
                <Icon
                  name="close"
                  display="block"
                  width={16}
                  height={16}
                  stroke={colors.waterBlue}
                />
              </Button>
            </Box>

            <LayoutSearchMenu
              height="100%"
              onSearch={() => setSearchOpen(false)}
              searchMode={Boolean(searchMode)}
              navSection={navSection}
              defaultSearchType={defaultSearchType}
            />
          </Box>
        </HideContainer>
      </PrintHidden>

      <Box
        position="relative"
        flex="1 1 auto"
        bg={darkMode && colors.fog}
        pb={[36, null, null, 54]}
        mb={darkMode && '-1px'}
        borderBottom={darkMode && `1px solid ${colors.aluminum}`}
        // eslint-disable-next-line react/jsx-props-no-spreading
        {...props}
      >
        {children}
      </Box>

      <PrintHidden>
        <ScrollWatcherContainer>
          {({ isInViewport }) => {
            if (isInViewport && !trackedScrollBottom) {
              trackEvent('Scroll: Bottom of Page', {
                reached_through: 'Scrolling',
              });
              setTrackedScrollBottom(true);
            }

            return <PageFooter url={pageUrl} hideDivider={hideFooterDivider} />;
          }}
        </ScrollWatcherContainer>
      </PrintHidden>
    </BaseLayout>
  );
}
